import { useState } from 'react';
import { FiX, FiEdit2, FiCheckCircle } from 'react-icons/fi';
import { useAuth } from '../../providers/auth-provider';
import toast from 'react-hot-toast';
import validator from 'validator';
import api from '../../libs/axios';

// Updated interfaces to match API response
export interface UserDetailsAttributes {
    id: number;
    user_id: number;
    first_name: string;
    last_name: string;
    email: string;
    date_of_birth: string;
    gender: string;
    profile_picture: string | null;
    address: string;
    city: string;
    state: string;
    country: string;
    pincode: string;
    created_at: string;
    updated_at: string;
}

export interface UserAttributes {
    id: number;
    phone_number: string;
    role: string;
    details: UserDetailsAttributes;
}

interface ValidationErrors {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone_number?: string;
    date_of_birth?: string;
    pincode?: string;
}

interface EditButtonsProps {
    isEditing: boolean;
    setIsEditing: (value: boolean) => void;
    onSave: () => void;
    editText?: string;
}

const EditButtons: React.FC<EditButtonsProps> = ({ isEditing, setIsEditing, onSave, editText = 'Edit details' }) => (
    <div className="flex items-center justify-between mb-6">
        <button
            onClick={() => setIsEditing(!isEditing)}
            className="flex items-center gap-2 text-primary-600 px-4 py-2 rounded-md border border-primary-600 hover:bg-gray-50"
        >
            {isEditing ? (
                <>
                    <FiX className="w-4 h-4" />
                    Close
                </>
            ) : (
                <>
                    <FiEdit2 className="w-4 h-4" />
                    {editText}
                </>
            )}
        </button>
        {isEditing && (
            <button
                onClick={onSave}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
                Save Changes
            </button>
        )}
    </div>
);

const AccountDetails: React.FC = () => {
    const [isEditing, setIsEditing] = useState(false);
    const { user, fetchUser } = useAuth();
    const [errors, setErrors] = useState<ValidationErrors>({});

    const [details, setDetails] = useState({
        first_name: user?.details?.first_name || '',
        last_name: user?.details?.last_name || '',
        email: user?.details?.email || '',
        phone_number: user?.phone_number || '',
        date_of_birth: user?.details?.date_of_birth || '',
        gender: user?.details?.gender || '',
        address: user?.details?.address || '',
        city: user?.details?.city || '',
        state: user?.details?.state || '',
        country: user?.details?.country || '',
        pincode: user?.details?.pincode || ''
    });

    const validateFields = (): boolean => {
        const newErrors: ValidationErrors = {};

        // First Name validation (required, only letters and spaces)
        if (!details.first_name.trim()) {
            newErrors.first_name = 'First name is required';
        } else if (!validator.isAlpha(details.first_name.replace(/\s/g, ''))) {
            newErrors.first_name = 'First name should contain only letters';
        } else if (details.first_name.trim().length < 2) {
            newErrors.first_name = 'First name must be at least 2 characters';
        }

        // Last Name validation (required, only letters and spaces)
        if (!details.last_name.trim()) {
            newErrors.last_name = 'Last name is required';
        } else if (!validator.isAlpha(details.last_name.replace(/\s/g, ''))) {
            newErrors.last_name = 'Last name should contain only letters';
        } else if (details.last_name.trim().length < 2) {
            newErrors.last_name = 'Last name must be at least 2 characters';
        }

        // Email validation (if provided, must be valid)
        if (details.email && !validator.isEmail(details.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        // Phone Number validation (if provided, must be valid mobile)
        if (details.phone_number) {
            const phoneWithoutCountryCode = details.phone_number.replace(/^\+91/, '');
            if (!validator.isMobilePhone(phoneWithoutCountryCode, 'en-IN')) {
                newErrors.phone_number = 'Please enter a valid mobile number';
            }
        }

        // Date of Birth validation (if provided, must be valid date and not future)
        if (details.date_of_birth) {
            if (!validator.isDate(details.date_of_birth)) {
                newErrors.date_of_birth = 'Please enter a valid date';
            } else if (validator.isAfter(details.date_of_birth, new Date().toISOString().split('T')[0])) {
                newErrors.date_of_birth = 'Date of birth cannot be in the future';
            }
        }

        // Pincode validation (if provided, must be valid pincode)
        if (details.pincode) {
            if (!validator.isPostalCode(details.pincode, 'any')) {
                newErrors.pincode = 'Please enter a valid pincode (6 digits)';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (validateFields()) {
            await api.put("/auth/profile", details);
            toast.success('Account details saved successfully!');
            fetchUser();
            console.log('Saving account details:', details);
            setIsEditing(false);
            setErrors({});
        } else {
            toast.error('Please fix the validation errors before saving');
        }
    };

    const handleFieldChange = (field: keyof typeof details, value: string) => {
        setDetails(prev => ({ ...prev, [field]: value }));

        // Clear error for this field when user starts typing
        if (errors[field as keyof ValidationErrors]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const getInputClassName = (fieldName: keyof ValidationErrors) => {
        const baseClass = `w-full p-2 border rounded-md ${!isEditing ? 'bg-gray-50' : ''}`;
        const errorClass = errors[fieldName] ? 'border-red-500' : 'border-gray-300';
        return `${baseClass} ${errorClass}`;
    };

    return (
        <div className="mx-auto p-4">
            <div className="bg-white rounded-lg shadow-sm border p-6">
                <h1 className="text-2xl font-semibold text-gray-800 mb-6">Account Details</h1>

                <EditButtons
                    isEditing={isEditing}
                    setIsEditing={setIsEditing}
                    onSave={handleSave}
                    editText="Edit account details"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm text-gray-600 mb-1">First Name *</label>
                        <input
                            type="text"
                            value={details.first_name}
                            onChange={(e) => handleFieldChange('first_name', e.target.value)}
                            disabled={!isEditing}
                            className={getInputClassName('first_name')}
                        />
                        {errors.first_name && (
                            <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm text-gray-600 mb-1">Last Name *</label>
                        <input
                            type="text"
                            value={details.last_name}
                            onChange={(e) => handleFieldChange('last_name', e.target.value)}
                            disabled={!isEditing}
                            className={getInputClassName('last_name')}
                        />
                        {errors.last_name && (
                            <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm text-gray-600 mb-1">Email</label>
                        <div className="relative">
                            <input
                                type="email"
                                value={details.email}
                                onChange={(e) => handleFieldChange('email', e.target.value)}
                                disabled={!isEditing}
                                className={`${getInputClassName('email')} pr-10`}
                            />
                        </div>
                        {errors.email && (
                            <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm text-gray-600 mb-1">Phone Number</label>
                        <div className="relative">
                            <span className="absolute left-2 border-r pr-1.5 top-1/2 -translate-y-1/2 text-gray-500">
                                +91
                            </span>
                            <input
                                type="text"
                                value={details.phone_number}
                                onChange={(e) => handleFieldChange('phone_number', e.target.value)}
                                disabled={!isEditing}
                                readOnly
                                className={`${getInputClassName('phone_number')} pl-12 pr-10 read-only:cursor-not-allowed read-only:bg-gray-50`}
                            />
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                <FiCheckCircle className="h-5 w-5 text-green-500" />
                            </div>
                        </div>
                        {errors.phone_number && (
                            <p className="text-red-500 text-xs mt-1">{errors.phone_number}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm text-gray-600 mb-1">Date of Birth</label>
                        <input
                            type="date"
                            value={details.date_of_birth}
                            onChange={(e) => handleFieldChange('date_of_birth', e.target.value)}
                            disabled={!isEditing}
                            className={getInputClassName('date_of_birth')}
                            max={new Date().toISOString().split('T')[0]}
                        />
                        {errors.date_of_birth && (
                            <p className="text-red-500 text-xs mt-1">{errors.date_of_birth}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm text-gray-600 mb-1">Gender</label>
                        <select
                            value={details.gender}
                            onChange={(e) => handleFieldChange('gender', e.target.value)}
                            disabled={!isEditing}
                            className={`w-full p-2 border rounded-md ${!isEditing ? 'bg-gray-50' : ''}`}
                        >
                            <option value="">Select Gender</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                            <option value="other">Other</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm text-gray-600 mb-1">Pincode</label>
                        <input
                            type="text"
                            value={details.pincode}
                            onChange={(e) => {
                                const re = /^[0-9\b]+$/;
                                if (e.target.value === '' || re.test(e.target.value)) {
                                    handleFieldChange('pincode', e.target.value);
                                }
                            }}
                            disabled={!isEditing}
                            className={getInputClassName('pincode')}
                            maxLength={6}
                        />
                        {errors.pincode && (
                            <p className="text-red-500 text-xs mt-1">{errors.pincode}</p>
                        )}
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-sm text-gray-600 mb-1">Address</label>
                        <textarea
                            value={details.address}
                            onChange={(e) => handleFieldChange('address', e.target.value)}
                            disabled={!isEditing}
                            rows={3}
                            className={`w-full p-2 border rounded-md ${!isEditing ? 'bg-gray-50' : ''}`}
                        />
                    </div>

                    <div>
                        <label className="block text-sm text-gray-600 mb-1">City</label>
                        <input
                            type="text"
                            value={details.city}
                            onChange={(e) => handleFieldChange('city', e.target.value)}
                            disabled={!isEditing}
                            className={`w-full p-2 border rounded-md ${!isEditing ? 'bg-gray-50' : ''}`}
                        />
                    </div>

                    <div>
                        <label className="block text-sm text-gray-600 mb-1">State</label>
                        <input
                            type="text"
                            value={details.state}
                            onChange={(e) => handleFieldChange('state', e.target.value)}
                            disabled={!isEditing}
                            className={`w-full p-2 border rounded-md ${!isEditing ? 'bg-gray-50' : ''}`}
                        />
                    </div>

                    <div>
                        <label className="block text-sm text-gray-600 mb-1">Country</label>
                        <input
                            type="text"
                            value={details.country}
                            onChange={(e) => handleFieldChange('country', e.target.value)}
                            disabled={!isEditing}
                            className={`w-full p-2 border rounded-md ${!isEditing ? 'bg-gray-50' : ''}`}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AccountDetails;